// Die linke Spalte der Freigabe: die ganze Besichtigung, nichts tief
// vergraben (E3, S3; Mockup /crm/freigabe/:id). Von oben nach unten:
//   - kam nach der Freigabe etwas dazu: das Band „bitte ansehen“ mit „Gesehen“;
//   - offene Fragen an dieser Besichtigung: das Band in Lime mit dem Sprung
//     zur ersten (seit S6; Gernots Entscheidung 2);
//   - die Kopf-Karte: Kunde, Adresse, Objekt, Anlage, Telefon und E-Mail
//     als Link, wer aufgenommen hat, wann gesendet und freigegeben, der
//     Montagetermin, der Weg zur Akte;
//   - die Dachplanung mit K2 – seit dem Augentest 21.10.2026 eine deutliche
//     Karte an festem Platz gleich unter der Kopf-Karte, mit „Ansehen“ (im
//     Browser) und „Herunterladen“ (lesbarer Name; DachplanungKarte.tsx);
//   - die Fragen zur Anlage (das ganze Projekt) und zur Besichtigung – lesen,
//     antworten, „erledigt“, fragen (seit S6, ../fragen/FragenAmStueck.tsx);
//   - der Platzhalter Drohnenfoto (Stufe 2);
//   - je Station ein Block: fertig oder deutlich „nicht fertig“ (oben die
//     Marke „1 offene Frage“, ein Klick springt hin), die Fotos mit
//     Zeichnung (ein Klick: groß – dort auch die Fragen zum Foto; auf der
//     Kachel die Marke), die Notizen mit Sprachmemos und ihren Fragen,
//     „Sonstiges“, die Längen, die Fragen zur Station, der Platzhalter
//     „Fachfragen – Stufe 2“;
//   - seit E3b (O65): je Station das Material von draußen (rechts, wo die
//     Längen stehen – die nur noch, wenn die Besichtigung welche hat), nach
//     den Stationen „Material am Schluss“.
//
// Fragen und Antworten gibt es für jede Rolle, die die Besichtigung sieht –
// auch Verkauf, Montage und Inbetriebnahme, die kein Material sehen
// (CLAUDE.md, Grundsatz 9).

import { useCallback, useEffect, useMemo, useState } from "react";
import { darf, PRODUKTLINIEN, STATIONEN, type BueroAntwort, type CrmAufnahme, type GesehenEingabe } from "@happy-energy/kern";
import { ApiFehler, Chip, Ikon, senden, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { adresseZeigen, bitteAnsehenGruende, kalendertagZeigen, kuerzel, nachtragMarke, objektArtName, standArt, wannZeigen, zeitpunktZeigen } from "../anzeige.ts";
import { StandChip } from "../bausteine.tsx";
import { FragenAmStueck, OffeneFragenMarke } from "../fragen/FragenAmStueck.tsx";
import { FragenBand } from "../fragen/FragenBand.tsx";
import {
  aeltesteZuerst,
  anzahl,
  fotoDerFrage,
  fragenAn,
  kachelZaehler,
  nachBezug,
  offenZahl,
  ortText,
  stationNrAn,
  type FragenJeStueck,
} from "../fragen/fragen.ts";
import { frageGemerkt, frageZeigen, gemerkteFrageNehmen } from "../fragen/sprung.ts";
import { FrageZeichen } from "../fragen/zeichen.tsx";
import { juengsteVonDraussen } from "../gesehen.ts";
import { fehlerText } from "../laden.tsx";
import { nurEigene } from "../navigation.ts";
import { anderenFensternMelden } from "../speicher.ts";
import { useCrm } from "../zusammenhang.tsx";
import { DachplanungKarte } from "./DachplanungKarte.tsx";
import { FotoBild, FotoGross, FotoZeichen, type FotoImZusammenhang } from "./fotos.tsx";
import { Laengen, MaterialVonDraussen, MemoListe, NotizText } from "./notizen.tsx";

function stationName(art: string): string {
  return STATIONEN.find((s) => s.schluessel === art)?.name ?? art;
}

/** „Seit der Freigabe ist etwas dazugekommen“ – mit „Gesehen“ (Gernots Entscheidung 5: keine zweite Freigabe). */
function BitteAnsehen({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  const { ich } = useCrm();
  const [sendet, setSendet] = useState(false);
  const gruende = bitteAnsehenGruende(a);
  const darfGesehen = darf(ich.nutzer.rolle, "freigeben");

  // „Gesehen“ gilt nur bis zu dem, was hier steht (seit S4, ../gesehen.ts):
  // Kam inzwischen noch ein Nachtrag oder eine neue Dachplanung, sagt der
  // Server „konflikt“ – dann zeigt die Seite den neuen Stand.
  async function gesehen() {
    const gezeigtBis = juengsteVonDraussen(a);
    if (gezeigtBis === null) return;
    setSendet(true);
    try {
      await senden<BueroAntwort>(`/api/crm/aufnahme/${a.id}/gesehen`, { gezeigtBis } satisfies GesehenEingabe);
      toastZeigen("Als gesehen vermerkt");
      anderenFensternMelden(a.id);
      neuLaden();
    } catch (f) {
      toastZeigen(fehlerText(f), "fehler");
      if (f instanceof ApiFehler && f.status === 409) neuLaden();
    } finally {
      setSendet(false);
    }
  }

  return (
    <div className="ansehen-band" role="status">
      <Ikon name="auge" groesse="g" />
      <div className="ansehen-wort">
        <b>Seit der Freigabe ist etwas dazugekommen – bitte ansehen.</b>
        <span>{gruende.length > 0 ? gruende.join(" · ") : "Ein Nachtrag oder eine geänderte Dachplanung."} Was neu ist, trägt unten die Marke „neu“.</span>
        {darfGesehen && <span className="ansehen-klein">Das Material änderst du rechts mit „Material ändern“ – die Unterlage zeigt dann die neue Liste.</span>}
      </div>
      {darfGesehen && (
        <button type="button" className="crm-knopf dunkel" onClick={() => void gesehen()} disabled={sendet}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Gesehen
        </button>
      )}
    </div>
  );
}

/**
 * Offene Fragen an dieser Besichtigung – das Band in Lime, nicht zu
 * übersehen (Gernots Entscheidung 2), mit dem Sprung zur ältesten: auf der
 * Seite dorthin rollen, oder – hängt sie an einem Foto – das Foto groß öffnen.
 */
function FragenHier({ a, zurFrage }: { a: CrmAufnahme; zurFrage: (frageId: string) => void }) {
  const offen = aeltesteZuerst(a.fragen.filter((f) => f.stand === "offen"));
  const erste = offen[0];
  if (!erste) return null;
  return (
    <FragenBand
      titel={`${anzahl(offen.length, "1 offene Frage", "# offene Fragen")} an dieser Besichtigung`}
      unter={`Die älteste: ${ortText(erste.bezug.tabelle, stationNrAn(erste.bezug, a))} – ${erste.von.name}, ${wannZeigen(erste.gestelltAm)}`}
      knopf={{ name: offen.length === 1 ? "Zur Frage" : "Zur ersten", los: () => zurFrage(erste.id) }}
    />
  );
}

/**
 * Die Fragen zum ganzen Projekt (Anlage) und zu dieser Besichtigung – oben,
 * unter der Kopf-Karte und der Dachplanung. An Stationen, Fotos und Notizen
 * fragt man dort, wo sie stehen. Ohne Erklär-Absatz (Augentest 21.10.2026):
 * Das Muster – Pille „Frage stellen“, Gespräch als Karte – sagt es selbst.
 */
function FragenOben({ a, gruppen, neuLaden }: { a: CrmAufnahme; gruppen: FragenJeStueck; neuLaden: () => void }) {
  const zurAnlage = { tabelle: "anlage", id: a.anlage.id } as const;
  const zurBesichtigung = { tabelle: "aufnahme", id: a.id } as const;
  const offen = offenZahl(a.fragen);
  return (
    <section className="crm-karte fragen-oben" aria-label="Fragen">
      <div className="karten-kopf">
        <FrageZeichen />
        <h2>Fragen</h2>
        <span className="leise klein">
          {a.fragen.length === 0 ? "noch keine" : `${anzahl(a.fragen.length, "1 Frage", "# Fragen")} an dieser Besichtigung · ${offen} offen`}
        </span>
      </div>
      <div className="fragen-abschnitt">
        <h3>Zur Anlage – das ganze Projekt</h3>
        <FragenAmStueck aufnahmeId={a.id} bezug={zurAnlage} fragen={fragenAn(gruppen, zurAnlage)} neuLaden={neuLaden} wo="zur Anlage" />
      </div>
      <div className="fragen-abschnitt">
        <h3>Zu dieser Besichtigung</h3>
        <FragenAmStueck aufnahmeId={a.id} bezug={zurBesichtigung} fragen={fragenAn(gruppen, zurBesichtigung)} neuLaden={neuLaden} wo="zur Besichtigung" />
      </div>
    </section>
  );
}

function KopfKarte({ a }: { a: CrmAufnahme }) {
  const { ich } = useCrm();
  const rolle = ich.nutzer.rolle;
  const produkt = PRODUKTLINIEN.find((p) => p.schluessel === a.anlage.produktlinie)?.name ?? a.anlage.produktlinie;
  const termin = a.anlage.montagetermin;
  return (
    <section className="crm-karte kopf-karte">
      <span className="avatar avatar-lime kopf-kuerzel">{kuerzel(a.kunde.name)}</span>
      <div className="kopf-karte-wort">
        <div>
          <h2>{a.kunde.name}</h2>
          <div className="leise">{adresseZeigen(a.objekt)}</div>
        </div>
        <div className="chip-zeile">
          <Chip ikon="haus">{objektArtName(a.objekt.art)}</Chip>
          <Chip ikon="sonne">{produkt}</Chip>
          {a.kunde.telefon && (
            <a className="chip chip-link" href={`tel:${a.kunde.telefon.replace(/[^\d+]/g, "")}`}>
              <Ikon name="telefon" groesse="k" /> {a.kunde.telefon}
            </a>
          )}
          {a.kunde.email && (
            <a className="chip chip-link" href={`mailto:${a.kunde.email}`}>
              <Ikon name="senden" groesse="k" /> {a.kunde.email}
            </a>
          )}
          <StandChip stand={standArt(a, !nurEigene(rolle))} />
        </div>
        <dl className="kopf-fakten">
          <div>
            <dt>Aufgenommen von</dt>
            <dd>{a.verkaeufer.name}</dd>
          </div>
          {a.gesendetAm && (
            <div>
              <dt>Gesendet</dt>
              <dd>{zeitpunktZeigen(a.gesendetAm)}</dd>
            </div>
          )}
          {a.freigegebenAm && (
            <div>
              <dt>Freigegeben</dt>
              <dd>
                {zeitpunktZeigen(a.freigegebenAm)}
                {a.freigegebenVon && ` · ${a.freigegebenVon.name}`}
              </dd>
            </div>
          )}
          {(termin.am || termin.text) && (
            <div>
              <dt>Montagetermin</dt>
              <dd>{[termin.am ? kalendertagZeigen(termin.am) : null, termin.text].filter(Boolean).join(" · ")}</dd>
            </div>
          )}
        </dl>
        {darf(rolle, "kunden") && (
          <a className="link-pfeil" href={`#/anlage/${a.anlage.id}`}>
            Ganze Akte der Anlage <Ikon name="rechts" groesse="k" />
          </a>
        )}
      </div>
    </section>
  );
}

function StationBlock({
  a,
  station,
  oeffnen,
  alleFotos,
  gruppen,
  neuLaden,
  zurFrage,
}: {
  a: CrmAufnahme;
  station: CrmAufnahme["stationen"][number];
  oeffnen: (fotoId: string) => void;
  alleFotos: readonly FotoImZusammenhang[];
  gruppen: FragenJeStueck;
  neuLaden: () => void;
  /** Zu einer Frage springen – auf der Seite hinrollen oder das Foto groß öffnen. */
  zurFrage: (frageId: string) => void;
}) {
  const fotos = alleFotos.filter((f) => f.foto.stationId === station.id);
  const notizen = a.notizen.filter((n) => n.stationId === station.id);
  const geaendert = station.geaendertImNachtrag ? nachtragMarke(station.geaendertImNachtrag, a) : null;
  const name = stationName(station.art);
  const zurStation = { tabelle: "station", id: station.id } as const;
  // Alle Fragen an dieser Station – an ihr selbst, ihren Fotos und Notizen: oben die Marke „1 offene Frage“.
  const fragenHier = a.fragen.filter((f) => stationNrAn(f.bezug, a) === station.nr);
  return (
    <section className={`station-block${station.fertig ? "" : " offen"}`} id={`station-${station.nr}`} aria-label={`Station ${station.nr}: ${name}`}>
      <div className="kopfzeile">
        <span className="nr">{station.nr}</span>
        <h3>{name}</h3>
        <span className="kopfzeile-rechts">
          <OffeneFragenMarke fragen={fragenHier} zu={zurFrage} wo={`an Station ${station.nr}`} />
          <Chip ikon="kamera">{fotos.length === 1 ? "1 Foto" : `${fotos.length} Fotos`}</Chip>
          {station.fertig ? (
            <Chip art="ok" ikon="haken">
              fertig
            </Chip>
          ) : (
            <Chip art="warn" ikon="achtung">
              nicht fertig
            </Chip>
          )}
        </span>
      </div>
      {!station.fertig && <p className="station-offen-satz">Der Verkäufer hat diese Station nicht als fertig markiert – hier fehlt vielleicht etwas.</p>}
      {geaendert && <p className="station-nachtrag">Im Nachtrag geändert: {geaendert.text.replace(/^neu – /, "")}</p>}

      {fotos.length > 0 ? (
        <div className="fotos">
          {fotos.map((f) => {
            const marke = nachtragMarke(f.foto.nachtragId, a);
            const fotoNotizen = a.notizen.filter((n) => n.fotoId === f.foto.id);
            const memos = a.memos.filter((m) => fotoNotizen.some((n) => n.id === m.notizId)).length;
            // Die Fragen stehen in der großen Ansicht – auf der Kachel nur, dass es welche gibt (am Foto und an seinen Notizen).
            const fragen = kachelZaehler([
              ...fragenAn(gruppen, { tabelle: "foto", id: f.foto.id }),
              ...fotoNotizen.flatMap((n) => fragenAn(gruppen, { tabelle: "notiz", id: n.id })),
            ]);
            return (
              <figure key={f.foto.id} className="foto-mit-notiz">
                <button
                  type="button"
                  className="foto-knopf"
                  onClick={() => oeffnen(f.foto.id)}
                  aria-label={`Foto ${f.nr} von ${f.von} an Station ${station.nr} groß ansehen${fragen ? ` – ${fragen.text}` : ""}`}
                >
                  <FotoBild foto={f.foto} kachel />
                  <FotoZeichen
                    gezeichnet={(f.foto.zeichnung?.striche.length ?? 0) > 0}
                    notiz={fotoNotizen.some((n) => n.text.trim() !== "")}
                    memos={memos}
                  />
                  {marke && (
                    <span className={`sendungs-marke foto-sendung${marke.neu ? "" : " alt"}`} title={marke.text}>
                      {marke.neu ? "neu" : "Nachtrag"}
                    </span>
                  )}
                  {fragen && (
                    <span className={`frage-marke foto-fragen-marke${fragen.offen > 0 ? "" : " still"}`} title="Die Fragen stehen in der großen Ansicht">
                      <FrageZeichen /> {fragen.text}
                    </span>
                  )}
                </button>
                {(fotoNotizen.some((n) => n.text.trim()) || memos > 0) && (
                  <figcaption>
                    {fotoNotizen
                      .map((n) => n.text.trim())
                      .filter(Boolean)
                      .join(" · ")}
                    {memos > 0 && (
                      <span className="foto-memo">
                        <Ikon name="mikro" groesse="k" /> {memos === 1 ? "Sprachmemo" : `${memos} Sprachmemos`}
                      </span>
                    )}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      ) : (
        <p className="leise klein">Keine Fotos an dieser Station.</p>
      )}

      <div className="daten">
        <div className="notizen">
          {notizen.length === 0 && (
            <div className="zitat">
              <div className="quelle">
                <Ikon name="notiz" groesse="k" /> Notiz zur Station
              </div>
              <p className="lese-text leer">Keine Notiz.</p>
            </div>
          )}
          {notizen.map((n) => (
            <div key={n.id} className="zitat">
              <div className="quelle">
                <Ikon name="notiz" groesse="k" /> Notiz zur Station
                <OffeneFragenMarke fragen={fragenAn(gruppen, { tabelle: "notiz", id: n.id })} zu={frageZeigen} wo="an dieser Notiz" />
              </div>
              <NotizText a={a} notiz={n} />
              <MemoListe a={a} notizId={n.id} />
              <FragenAmStueck
                aufnahmeId={a.id}
                bezug={{ tabelle: "notiz", id: n.id }}
                fragen={fragenAn(gruppen, { tabelle: "notiz", id: n.id })}
                neuLaden={neuLaden}
                wo="zu dieser Notiz"
              />
            </div>
          ))}
          {station.sonstiges?.trim() && (
            <div className="zitat">
              <div className="quelle">
                <Ikon name="stift" groesse="k" /> Sonstiges
              </div>
              <p className="lese-text">{station.sonstiges}</p>
            </div>
          )}
        </div>
        <div className="daten-rechts">
          {/* Längen nur, wo die Besichtigung welche hat (vor E3b aufgenommen) – sonst ist es Material. */}
          {a.laengen.length > 0 && <Laengen a={a} stationId={station.id} />}
          <MaterialVonDraussen a={a} stationId={station.id} />
          {a.laengen.length === 0 && !(a.material ?? []).some((m) => m.stationId === station.id) && <p className="leise klein">Kein Material von hier.</p>}
        </div>
      </div>
      <div className="station-fragen">
        <FragenAmStueck aufnahmeId={a.id} bezug={zurStation} fragen={fragenAn(gruppen, zurStation)} neuLaden={neuLaden} wo={`zu Station ${station.nr}`} />
      </div>
      <div>
        <Chip art="sp" ikon="uhr">
          Fachfragen – Stufe 2
        </Chip>
      </div>
    </section>
  );
}

/** Die ganze Besichtigung – die linke Spalte der Freigabe (für Rollen ohne Material die ganze Seite). */
export function AufnahmeAnsicht({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  const stationen = useMemo(() => [...a.stationen].sort((x, y) => x.nr - y.nr), [a.stationen]);
  // Alle Fotos in der Reihenfolge der Seite: nach Station, darin wie aufgenommen – zum Blättern in der großen Ansicht.
  const fotos = useMemo<FotoImZusammenhang[]>(
    () =>
      stationen.flatMap((s) => {
        const hier = a.fotos.filter((f) => f.stationId === s.id).sort((x, y) => x.reihenfolge - y.reihenfolge);
        return hier.map((foto, i) => ({ foto, station: `Station ${s.nr} · ${stationName(s.art)}`, nr: i + 1, von: hier.length }));
      }),
    [a.fotos, stationen],
  );
  const [gross, setGross] = useState<number | null>(null);
  // Die Fragen nach dem Stück, an dem sie hängen – jede steht genau dort (Grundsatz 9).
  const gruppen = useMemo(() => nachBezug(a.fragen), [a.fragen]);

  /** Zu einer Frage: an einem Foto (oder einer Notiz zum Foto) das Foto groß öffnen, sonst auf der Seite hinrollen. */
  const zurFrage = useCallback(
    (frageId: string) => {
      const f = a.fragen.find((x) => x.id === frageId);
      if (!f) return;
      const fotoId = fotoDerFrage(f.bezug, a.notizen);
      const i = fotoId === null ? -1 : fotos.findIndex((x) => x.foto.id === fotoId);
      if (i >= 0) {
        setGross(i);
        frageZeigen(frageId, 150);
      } else {
        frageZeigen(frageId);
      }
    },
    [a.fragen, a.notizen, fotos],
  );

  // Von der Seite „Fragen“ hierher (Klick auf den Kunden): nach dem Laden an die Frage springen (../fragen/sprung.ts).
  useEffect(() => {
    if (!frageGemerkt(a.id)) return;
    const uhr = window.setTimeout(() => {
      const frageId = gemerkteFrageNehmen(a.id);
      if (frageId) zurFrage(frageId);
    }, 80);
    return () => window.clearTimeout(uhr);
  }, [a.id, zurFrage]);

  return (
    <div className="aufnahme-spalte">
      {a.hinweis === "bitte-ansehen" && <BitteAnsehen a={a} neuLaden={neuLaden} />}
      {a.hinweis === "nachtrag" && (
        <Tipp art="gelb" ikon="plus">
          Der Verkauf hat etwas nachgereicht. Was mit einem Nachtrag kam, trägt unten die Marke <span className="sendungs-marke">neu – Nachtrag vom …</span>
        </Tipp>
      )}
      <FragenHier a={a} zurFrage={zurFrage} />
      <KopfKarte a={a} />
      <DachplanungKarte a={a} neuLaden={neuLaden} />
      <FragenOben a={a} gruppen={gruppen} neuLaden={neuLaden} />
      <Spaeter titel="Drohnenfoto vom Haus" wann="Stufe 2">
        Das Foto von oben, das der Verkäufer beim Kunden macht, liegt dann hier.
      </Spaeter>
      {stationen.map((s) => (
        <StationBlock
          key={s.id}
          a={a}
          station={s}
          alleFotos={fotos}
          gruppen={gruppen}
          neuLaden={neuLaden}
          zurFrage={zurFrage}
          oeffnen={(id) => setGross(fotos.findIndex((f) => f.foto.id === id))}
        />
      ))}
      {(a.material ?? []).some((m) => m.stationId === null) && (
        <section className="station-block" aria-label="Material am Schluss">
          <div className="kopfzeile">
            <span className="nr">
              <Ikon name="paket" groesse="k" />
            </span>
            <h3>Material am Schluss</h3>
          </div>
          <MaterialVonDraussen a={a} stationId={null} titel="Vom Verkauf auf der Seite „Material“ angegeben" />
        </section>
      )}
      {gross !== null && gross >= 0 && (
        <FotoGross a={a} fotos={fotos} index={gross} setIndex={setGross} schliessen={() => setGross(null)} gruppen={gruppen} neuLaden={neuLaden} />
      )}
      <NachtragMarkeHinweis a={a} />
    </div>
  );
}

/** Ganz unten, klein: wann welche Nachträge kamen – damit „Nachtrag vom …“ einen Bezug hat. */
function NachtragMarkeHinweis({ a }: { a: CrmAufnahme }) {
  const gesendet = a.nachtraege.filter((n) => n.gesendetAm !== null);
  if (gesendet.length === 0) return null;
  return <p className="leise klein nachtraege-liste">Nachträge im Büro: {gesendet.map((n) => wannZeigen(n.gesendetAm!)).join(" · ")}</p>;
}
